import { Control } from "react-hook-form"
import InputMask from "react-input-mask"
import { Icon } from "@/components/Atoms/Icon"
import {
  hasDigits,
  isValidBirthDate,
  isValidCPF,
  isValidEmail,
} from "@/modules/Users/utils/dataTransformers"

// Componentes Shadcn/UI
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"

interface MemberFormFieldsProps {
  control: Control
  mode: "create" | "edit"
}

const validateBirthDate = (value: string) => {
  if (!value) return true
  return isValidBirthDate(value) || "Data inválida"
}

const validateCPF = (value: string) => {
  if (!value) return true
  return isValidCPF(value) || "CPF inválido"
}

const validateEmail = (value: string) => {
  if (!value) return true
  return isValidEmail(value) || "Email inválido"
}

const validateDigits =
  (length: number, mensagem: string, obrigatorio: boolean) => (value: string) => {
    if (!(value ?? "").replace(/\D/g, "")) return obrigatorio ? "Campo obrigatório" : true
    return hasDigits(value, length) || mensagem
  }

const RequiredMark = () => <span className="text-destructive"> *</span>

export function MemberFormFields({ control, mode }: MemberFormFieldsProps) {
  // Helper para renderizar os títulos das seções
  const SectionTitle = ({ icon, title }: { icon: string; title: string }) => (
    <div className="mb-4 flex items-center gap-2 border-b border-zinc-800 pb-2">
      <Icon
        name={icon}
        className="h-4 w-4 text-orange-500"
      />
      <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400">{title}</h3>
    </div>
  )

  return (
    <div className="space-y-10 duration-500 animate-in fade-in slide-in-from-bottom-4">
      {/* SEÇÃO 1: Identificação Básica */}
      <section className="rounded-xl border border-white/20 bg-zinc-900/20 p-6">
        <SectionTitle
          icon="user"
          title="Identificação"
        />
        <div className="grid grid-cols-1 gap-6">
          <FormField
            control={control}
            name="nome_completo"
            rules={{ required: "Campo obrigatório" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Nome Completo
                  <RequiredMark />
                </FormLabel>
                <FormControl>
                  <Input
                    className="bg-zinc-950/50"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={control}
              name="email"
              rules={{ required: "Campo obrigatório", validate: validateEmail }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email Corporativo
                    <RequiredMark />
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="bg-zinc-950/50"
                      placeholder="email@dominio.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="cpf"
              rules={{ required: "Campo obrigatório", validate: validateCPF }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    CPF
                    <RequiredMark />
                  </FormLabel>
                  <FormControl>
                    <InputMask
                      mask="999.999.999-99"
                      value={field.value}
                      onChange={field.onChange}
                    >
                      {(inputProps: any) => (
                        <Input
                          className="bg-zinc-950/50"
                          {...inputProps}
                        />
                      )}
                    </InputMask>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </section>

      {/* SEÇÃO 2: Eventos e Personalização */}
      <section className="rounded-xl border border-white/20 bg-zinc-900/20 p-6">
        <SectionTitle
          icon="award"
          title="Eventos"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name="nome_certificado"
            rules={{ required: mode === "create" && "Campo obrigatório" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Nome para Certificado
                  {mode === "create" && <RequiredMark />}
                </FormLabel>
                <FormControl>
                  <Input
                    className="bg-zinc-950/50"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="nome_cracha"
            rules={{ required: mode === "create" && "Campo obrigatório" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Nome para Crachá
                  {mode === "create" && <RequiredMark />}
                </FormLabel>
                <FormControl>
                  <Input
                    className="bg-zinc-950/50"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </section>

      {/* SEÇÃO 3: Dados Pessoais (Campos detalhados) */}
      <section className="rounded-xl border border-white/20 bg-zinc-900/20 p-6">
        <SectionTitle
          icon="map-pin"
          title="Dados Pessoais"
        />
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={control}
              name="cep"
              rules={{
                required: mode === "create" && "Campo obrigatório",
                validate: validateDigits(8, "CEP incompleto", mode === "create"),
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    CEP
                    {mode === "create" && <RequiredMark />}
                  </FormLabel>
                  <FormControl>
                    <InputMask
                      mask="99999-999"
                      value={field.value}
                      onChange={field.onChange}
                    >
                      {(inputProps: any) => (
                        <Input
                          className="bg-zinc-950/50"
                          {...inputProps}
                        />
                      )}
                    </InputMask>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="telefone"
              rules={{
                required: mode === "create" && "Campo obrigatório",
                validate: validateDigits(11, "Telefone incompleto", mode === "create"),
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Telefone
                    {mode === "create" && <RequiredMark />}
                  </FormLabel>
                  <FormControl>
                    <InputMask
                      mask="(99) 99999-9999"
                      value={field.value}
                      onChange={field.onChange}
                    >
                      {(inputProps: any) => (
                        <Input
                          className="bg-zinc-950/50"
                          {...inputProps}
                        />
                      )}
                    </InputMask>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField
              control={control}
              name="sexo"
              rules={{ required: mode === "create" && "Campo obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Sexo
                    {mode === "create" && <RequiredMark />}
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="bg-zinc-950/50">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="data_nascimento"
              rules={{
                required: mode === "create" && "Campo obrigatório",
                validate: validateBirthDate,
              }}
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>
                    Data de Nascimento
                    {mode === "create" && <RequiredMark />}
                  </FormLabel>
                  <FormControl>
                    <InputMask
                      mask="99/99/9999"
                      value={field.value}
                      onChange={field.onChange}
                    >
                      {(inputProps: any) => (
                        <Input
                          className="bg-zinc-950/50"
                          {...inputProps}
                        />
                      )}
                    </InputMask>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* <FormField
            control={control}
            name="chocolate_preferido"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Chocolate Preferido</FormLabel>
                <FormControl>
                  <Input
                    className="bg-zinc-950/50"
                    placeholder="Ex: Amargo 70%"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}
        </div>
      </section>

      {/* SEÇÃO 4: Cargo e Permissões */}
      <section className="rounded-xl border border-white/20 bg-zinc-900/20 p-6">
        <SectionTitle
          icon="briefcase"
          title="Profissional e Acesso"
        />
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={control}
              name="cargo"
              rules={{ required: "Campo obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Cargo
                    <RequiredMark />
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="bg-zinc-950/50"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="departamento"
              rules={{ required: mode === "create" && "Campo obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Departamento
                    {mode === "create" && <RequiredMark />}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="bg-zinc-950/50"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={control}
            name="perfil"
            rules={{ required: "Campo obrigatório" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Perfil de Acesso
                  <RequiredMark />
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="bg-zinc-950/50">
                      <SelectValue placeholder="Selecione o perfil" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Liderança">Liderança</SelectItem>
                    <SelectItem value="Colaborador">Colaborador</SelectItem>
                    <SelectItem value="Convidado">Convidado</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={control}
              name="administrador"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border border-white/20 bg-zinc-950/30 p-4">
                  <FormLabel className="text-sm font-medium">PAG</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="status"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border border-white/20 bg-zinc-950/30 p-4">
                  <FormLabel
                    className={`text-sm font-medium ${field.value ? "text-green-500" : "text-red-500"}`}
                  >
                    {field.value ? "Ativo" : "Inativo"}
                  </FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </section>
    </div>
  )
}
